/**
 * ── CONVERT A QUOTE INTO THE INVOICE THAT BILLS IT ────────────────────────────────────────────
 *
 * Convert used to fire straight off the drawer button. It now opens here first, because the
 * requester wants the customer's purchase order captured at the moment of billing (2026-09-21:
 * "Once the user try to convert it, can you pop out window to enter the PO number").
 *
 * ⚠️ A DIALOG IN FRONT OF AN IRREVERSIBLE ACT IS NOT A COST HERE, IT IS THE POINT. Converting
 * accepts the quote in Stripe and arms an invoice that emails itself about an hour later. The
 * previous one-click version had no confirmation at all; asking for the PO also gives an operator
 * the half-second in which "wrong row" becomes visible.
 *
 * ── WHAT THIS DIALOG DOES NOT OFFER ───────────────────────────────────────────────────────────
 *
 * Any way to change the money. Converting bills exactly what the quote promised — that is the
 * whole distinction between this and Raise the invoice, and it is why the server takes no amounts
 * here. A price that must move goes through Supersede, where the customer sees the new figure.
 *
 * The same reasoning as `SupersedeQuoteDialog`'s missing price field, from the other side.
 */
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@staff-app/ui';
import { formatMinor, intlRequestNo, type IntlRequest } from '@staff-app/shared-types';
import { useConvertIntlQuote } from '../hooks/useIntlRequests';
import { PoAttachment, uploadPoDocument, type PoValues } from './PoAttachment';

export function ConvertQuoteDialog({
  request,
  open,
  onOpenChange,
}: {
  request: IntlRequest | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [po, setPo] = useState<PoValues>({ poNumber: '', file: null });
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [convert, { loading, error }] = useConvertIntlQuote();

  if (!request) return null;
  const busy = loading || uploading;

  const submit = async () => {
    if (busy) return;
    setUploadError(null);

    /**
     * THE UPLOAD GOES FIRST, AND A FAILURE HERE STOPS EVERYTHING.
     *
     * If it went second, a failed upload would leave the customer billed with the paperwork
     * missing — and the operator staring at an error that makes it look as though nothing
     * happened. This way the worst case is a document filed against an invoice that was never
     * raised, which is a harmless orphan somebody can delete.
     */
    let poDocumentId: string | undefined;
    if (po.file) {
      setUploading(true);
      try {
        poDocumentId = await uploadPoDocument(request, po);
      } catch (e) {
        setUploadError(
          `The purchase order could not be filed: ${(e as Error).message}. Nothing has been ` +
            'billed — the quote is untouched.',
        );
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    try {
      await convert({
        variables: {
          input: {
            requestId: request.id,
            poNumber: po.poNumber.trim() || undefined,
            poDocumentId,
          },
        },
      });
      setPo({ poNumber: '', file: null });
      onOpenChange(false);
    } catch {
      // Rendered from the hook's `error`; the dialog stays open with the typed PO intact.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="intl-convert-dialog">
        <DialogHeader>
          <DialogTitle>{`Convert the quote on ${intlRequestNo(request.id)}`}</DialogTitle>
          <DialogDescription>
            {request.quoteNumber ? `${request.quoteNumber} — ` : ''}
            {request.quoteTotal != null
              ? formatMinor(request.quoteTotal, request.quoteCurrency ?? 'usd')
              : 'the quoted amount'}
            {'. This bills exactly what the customer was quoted.'}
          </DialogDescription>
        </DialogHeader>

        <PoAttachment request={request} values={po} onChange={setPo} disabled={busy} />

        <p className="text-xs text-muted-foreground">
          {'Converting accepts the quote in Stripe and arms the invoice’s auto-send. Stripe '}
          {'finalizes and emails it to the customer about an hour later, with no further action '}
          {'from you.'}
        </p>

        {uploadError ? (
          <p className="text-sm text-destructive" data-testid="intl-convert-upload-error">
            {uploadError}
          </p>
        ) : null}
        {error ? (
          <p className="text-sm text-destructive" data-testid="intl-convert-error">
            {error.message}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {'Cancel'}
          </Button>
          <Button onClick={submit} disabled={busy} data-testid="intl-convert-save">
            {uploading ? 'Filing the PO…' : loading ? 'Converting…' : 'Convert to an invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
